import React, { useState } from 'react';
import { useTrainingPlan } from '@/features/training/hooks/useTrainingPlan';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import './ScheduleSection.css';

import type { TrainingDay } from '@/features/training/model/trainingSlice';

export const ScheduleSection: React.FC = () => {
    const { plan, loading, error } = useTrainingPlan();
    const [activeWeek, setActiveWeek] = useState(0);

    if (!plan || loading || error) return null;

    const days: (TrainingDay & { dayOfMonth: number })[] = plan.plan.days.map((day, index) => ({
        ...day,
        dayOfMonth: index + 1,
    }));

    const weeks: (TrainingDay & { dayOfMonth: number })[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    const formatOneMonthRange = (createdAt: string) => {
        const startDate = new Date(createdAt);
        const endDate = new Date(startDate);
        endDate.setMonth(startDate.getMonth() + 1);

        const format = (date: Date) =>
            date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            });

        return `${format(startDate)}–${format(endDate)}`;
    };


    return (
        <section className="section layout__section program">
            <div className="container">
                <div className="program-intro-wrapper">
                    <div className="program-intro__line" />
                    <div className="programTop">
                        <div className="program-intro__left">
                            <h1 className="program-intro__title">План тренировок</h1>
                            <div className="program-intro__row">
                                <span className="badge badge_purple">НА 1-Й МЕСЯЦ</span>
                                <span className="program-intro__date">
  {formatOneMonthRange(plan.created_at)}
</span>

                            </div>
                            <p className="program-intro__subtitle">Подобранная для вас по итогу теста</p>
                        </div>
                        <div className="program-intro__image">
                            <img src="/img/program-image2.png" alt="Аватар" />
                        </div>
                    </div>
                    <div className="program-intro">
                        <div className="program-intro__left">
                            <p className="program-intro__description bold">
                                Учитывая ваши ответы, вам требуется<br />
                                укрепить мышцы спины, рук, ног и добавить<br />
                                кардио тренировки 3 раза в неделю.
                            </p>
                        </div>
                        <div className="program-intro__center">
                            <p className="program-intro__text">
                                Мы подобрали для вас персональный тренировочный план на месяц. Ниже представлено расписание с упражнениями на каждый день. Видеозаписи помогут правильно выполнять технику.
                            </p>
                        </div>
                    </div>
                </div>

                <nav className="program-schedule__tabs-nav program-schedule-tabs-nav">
                    <span className="program-schedule-tabs-nav__label">Расписание тренировок</span>
                    <ul className="tabs-nav__list program-schedule-tabs-nav__list">
                        {weeks.map((_, i) => (
                            <li
                                key={i}
                                className={`tabs-nav__item program-schedule-tabs-nav__item ${activeWeek === i ? '_active' : ''}`}
                                onClick={() => setActiveWeek(i)}
                            >
                                <a className="tabs-nav__link program-schedule-tabs-nav__link">
                                    {i + 1} неделя
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="program__cards program-cards">
                    <Swiper
                        className="slider program-cards__slider"
                        slidesPerView={'auto'}
                        spaceBetween={30}
                        navigation={{
                            nextEl: '.slider-nav__button_next',
                            prevEl: '.slider-nav__button_prev',
                        }}
                        modules={[Navigation]}
                    >
                        {weeks[activeWeek]?.map((day, idx) => (
                            <SwiperSlide key={idx} style={{ width: 350 }}>
                                <div className="program-cards-grid">
                                <div className={`program-card S`}>
                                    <header className="program-card__header">
                                        <h4 className="title program-card__title">{`День ${day.dayOfMonth}`}</h4>
                                    </header>
                                    <div className="program-card__content">
                                        {day.rest ? (
                                            <p className="program-card__text">День отдыха</p>
                                        ) : (
                                            <ul className="program-card__list">
                                                <li className="program-card__item program-card-item">
                                                    <span className="program-card-item__time">{day.DayTime == 'day' ? 'День' : 'Утро'}</span>
                                                    <div className="program-card-item__tasks">
                                                        {Object.entries(day.Exercises || {}).map(([title, duration], i) => (
                                                            <div className="program-card-item__task program-card-item-task" key={i}>
                                                                <h5 className="title program-card-item-task__title">
                                                                    <span>{i + 1}/</span> {title}
                                                                </h5>
                                                                <div className="program-card-item-task__group">
                                                                    <span className="program-card-item-task__duration">{String(duration)}</span>
                                                                    <a
                                                                        className="program-card-item-task__video js-open-video"
                                                                        href="javascript:void(0)"
                                                                        data-name={title}
                                                                    >
                                                                        Видео
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </li>
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div className="slider__nav slider-nav">
                        <div className="slider-nav__button slider-nav__button_prev">
                            <svg className="svg-icon">
                                <use href="/img/sprite-icons.svg#arrow-left"></use>
                            </svg>
                        </div>
                        <div className="slider-nav__separator"></div>
                        <div className="slider-nav__button slider-nav__button_next">
                            <svg className="svg-icon">
                                <use href="/img/sprite-icons.svg#arrow-right"></use>
                            </svg>
                        </div>
                    </div>

                    <button style={{ display: 'flex', margin: '84px auto 140px' }} className="button">
                        Перейти к плану на день
                    </button>
                </div>
            </div>
        </section>
    );
};
